import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, Badge, Button, Spinner } from '@tkpod/ui';
import { OrgPermissions, parsePermission } from '@tkpod/shared';
import { api } from '@/lib/api';

interface PortalRole {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isSystemRole: boolean;
  permissions: string[];
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

const ALL_ORG_PERMISSIONS = Object.values(OrgPermissions);

function computeEffectivePermissions(stored: string[]): string[] {
  const effective = new Set<string>();
  const manageResources = new Set<string>();

  for (const perm of stored) {
    effective.add(perm);
    const { resource, action } = parsePermission(perm);
    if (action === 'manage') {
      manageResources.add(resource);
    }
  }

  for (const perm of ALL_ORG_PERMISSIONS) {
    const { resource } = parsePermission(perm);
    if (manageResources.has(resource)) {
      effective.add(perm);
    }
  }

  return [...effective];
}

function groupByResource(permissions: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const perm of permissions) {
    const { resource, action } = parsePermission(perm);
    if (!groups.has(resource)) groups.set(resource, []);
    groups.get(resource)!.push(action);
  }
  return groups;
}

function formatResourceLabel(resource: string): string {
  return resource
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function PortalRolesPage() {
  const queryClient = useQueryClient();
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const { data, isLoading } = useQuery<{ data: PortalRole[] }>({
    queryKey: ['portal-roles'],
    queryFn: () => api.get<{ data: PortalRole[] }>('/api/v1/admin/portal-roles'),
  });

  const roles = data?.data ?? [];

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/portal-roles/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-roles'] });
      setEditingRoleId(null);
    },
  });

  const rolesWithEffective = useMemo(
    () =>
      roles.map((role) => {
        const effective = computeEffectivePermissions(role.permissions);
        return {
          ...role,
          storedCount: role.permissions.length,
          effectiveCount: effective.length,
          effectivePermissions: effective,
          grouped: groupByResource(effective),
        };
      }),
    [roles],
  );

  const openEdit = (role: PortalRole) => {
    setEditingRoleId(role.id);
    setEditName(role.name);
    setEditDescription(role.description ?? '');
  };

  const handleSave = () => {
    if (!editingRoleId) return;
    const body: Record<string, unknown> = {};
    const originalRole = roles.find((r) => r.id === editingRoleId);
    if (editName !== originalRole?.name) body.name = editName;
    if (editDescription !== (originalRole?.description ?? '')) body.description = editDescription || null;
    if (Object.keys(body).length === 0) { setEditingRoleId(null); return; }
    updateMutation.mutate({ id: editingRoleId, body });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Portal Roles</h1>
        <p className="mt-1 text-sm text-text-secondary">
          System-defined roles for portal organization members.
        </p>
      </div>

      <Card>
        <CardHeader
          title="Organization Roles"
          description="These roles are assigned to portal organization members."
        />
        <div className="divide-y divide-border">
          {rolesWithEffective.map((role) => {
            const isExpanded = expandedRole === role.slug;
            const isEditing = editingRoleId === role.id;

            return (
              <div key={role.slug}>
                <div className="flex w-full items-center justify-between px-6 py-4">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedRole(isExpanded ? null : role.slug)
                    }
                    className="flex flex-1 items-center justify-between text-left transition-colors hover:bg-surface-secondary rounded-md px-2 py-1 -mx-2"
                  >
                    <div>
                      {isEditing ? (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="rounded border border-border bg-surface px-2 py-1 text-sm font-medium text-text-primary"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-primary">
                            {role.name}
                          </p>
                          {role.isSystemRole && <Badge variant="warning" size="sm">System</Badge>}
                        </div>
                      )}
                      {isEditing ? (
                        <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                          <input
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="w-full rounded border border-border bg-surface px-2 py-1 text-xs text-text-secondary"
                            placeholder="Description..."
                          />
                        </div>
                      ) : (
                        <p className="mt-0.5 text-xs text-text-secondary">
                          {role.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-medium text-text-primary">
                          {role.effectiveCount}
                        </p>
                        <p className="text-xs text-text-tertiary">
                          effective permissions
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-text-primary">
                          {role.userCount}
                        </p>
                        <p className="text-xs text-text-tertiary">users</p>
                      </div>
                      <svg
                        className={`h-4 w-4 text-text-tertiary transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>
                  <div className="ml-3 flex gap-1">
                    {isEditing ? (
                      <>
                        <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                          {updateMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingRoleId(null)}>Cancel</Button>
                      </>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(role)}>Edit</Button>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-border bg-surface-secondary/50 px-6 py-4 space-y-3">
                    {role.storedCount !== role.effectiveCount && (
                      <p className="text-xs text-text-tertiary">
                        {role.storedCount} stored permission{role.storedCount !== 1 ? 's' : ''} (including <code className="rounded bg-surface-tertiary px-1 py-0.5">:manage</code> wildcards) expand to {role.effectiveCount} effective permissions.
                      </p>
                    )}
                    {[...role.grouped.entries()]
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([resource, actions]) => (
                        <div key={resource}>
                          <p className="text-xs font-semibold text-text-primary mb-1">
                            {formatResourceLabel(resource)}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {actions.sort().map((action) => (
                              <Badge
                                key={`${resource}:${action}`}
                                variant={action === 'manage' ? 'primary' : 'default'}
                                size="sm"
                              >
                                {action}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
